// frontend/src/pages/employees/TrendStrip.jsx
import React from "react";
import { hours as fmtHours } from "./format";
import { useAnalytics } from "./useAnalytics";
import { periodBounds, windowDayCount } from "./usePeriod";

// Beyond ~62 days a by=day strip renders 60+ 1-pixel-wide bars -- a
// two-year Custom window can reach 700+. usePeriod owns every date
// calculation in this feature, so the threshold is applied to a count
// windowDayCount already computed, not to a span TrendStrip works out
// itself.
const DAY_BUCKET_LIMIT = 62;

const panel = {
  background: "white", borderRadius: 12, padding: 24,
  boxShadow: "0 1px 3px rgba(0,0,0,0.1)", marginTop: 24,
};
const bar = {
  display: "flex", flexDirection: "column", justifyContent: "flex-end",
  alignItems: "center", flex: "1 1 0", minWidth: 0, gap: 4,
  background: "none", border: "none", cursor: "pointer", padding: 0,
  height: 120, font: "inherit",
};

/**
 * Hours over time, following the period control.
 *
 * Absorbs the old Monthly tab. A year shows its months, a month shows
 * its days, and a day shows nothing -- there is no trend within a single
 * day worth a chart, and the strip is hidden rather than rendered empty.
 */
export default function TrendStrip({
  granularity, periodLabel, employeeId, startDate, endDate, onBarClick,
}) {
  // Hooks cannot be called conditionally, so the fetch is suppressed by
  // passing a null path rather than by returning early above it. A day
  // granularity has nothing to trend; a custom window whose from === to
  // is the same case by another name and is hidden for the same reason.
  const singleDayCustom = granularity === "custom" && startDate === endDate;
  const active = granularity !== "day" && !singleDayCustom;

  // Bucketed by the window's actual LENGTH, not by the granularity's
  // name: a year is always >62 days (by=month) and a plain month is
  // always <=62 (by=day), so this reduces to the old behaviour for
  // day/month/year -- but a long Custom window (the whole reason Custom
  // exists) now also falls back to by=month instead of rendering 700+
  // 1-pixel bars.
  const by = active && windowDayCount(startDate, endDate) > DAY_BUCKET_LIMIT
    ? "month" : "day";

  const params = { by, start_date: startDate, end_date: endDate };
  if (employeeId) params.employee = employeeId;

  const { data, loading, error } = useAnalytics(
    active ? "breakdown/" : null, params);

  if (!active) return null;

  const rows = (data && data.rows) || [];
  const max = rows.reduce((m, r) => Math.max(m, Number(r.hours) || 0), 0);
  // Named after the actual period, not "this month" -- usePeriod already
  // derives this label (PersonView passes it down), so TrendStrip owns no
  // date logic of its own here. `startDate.slice(0, 4)` is TrendStrip's
  // own pre-existing derivation for when no periodLabel was passed, left
  // as is. The heading reflects the bucket actually fetched (`by`), not
  // the granularity name, since the two can now disagree for Custom.
  const heading = by === "month"
    ? `${periodLabel || String(startDate).slice(0, 4)} by month`
    : `${periodLabel || "This month"} by day`;

  return (
    <div style={panel}>
      <h3 style={{ marginTop: 0, marginBottom: 16, fontSize: 16 }}>
        Trend — {heading}
      </h3>

      {loading && <div style={{ color: "#6b7280" }}>Loading…</div>}
      {error && <div role="alert" style={{ color: "#b91c1c" }}>{error}</div>}

      {!loading && !error && rows.length === 0 && (
        <div style={{ color: "#6b7280" }}>No time in this period.</div>
      )}

      {!loading && !error && rows.length > 0 && (
        <div style={{ display: "flex", gap: 4, alignItems: "flex-end" }}>
          {rows.map((r) => (
            <button
              key={r.label}
              type="button"
              style={bar}
              aria-label={`${r.label}, ${fmtHours(r.hours)} hours`}
              onClick={() => {
                // A bar narrows TIME: it hands back a range and no
                // dimension filter. periodBounds does the month/day
                // arithmetic so this component owns none of its own.
                const g = by === "month" ? "month" : "day";
                const anchor = by === "month" ? `${r.label}-01` : r.label;
                const { startDate: s, endDate: e } = periodBounds(g, anchor);
                onBarClick && onBarClick({
                  startDate: s, endDate: e, label: r.label,
                });
              }}
            >
              <span
                aria-hidden="true"
                style={{
                  width: "100%", background: "#3b82f6", borderRadius: "3px 3px 0 0",
                  height: max > 0
                    ? `${Math.max(2, (Number(r.hours) / max) * 100)}%`
                    : "2px",
                }}
              />
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
